import { Component, Input, Output, EventEmitter } from '@angular/core';
import { SEMH_COLOURS } from '../models/leap-v2-semh';

// Red → green ramp used for the value text when colourRamp is enabled (SEMH scales).
const COLOUR_RAMP = SEMH_COLOURS;

/**
 * Reusable labelled range slider used across the ILP for the SEMH ratings,
 * the key-stage scale and the percentage scores. The parent renders the field
 * label; this component owns the track, the tick labels and the value readout.
 */
@Component({
  selector: 'leap-v2-scale-slider',
  templateUrl: './leap-v2-scale-slider.component.html',
  styleUrls: ['./leap-v2-scale-slider.component.css'],
  standalone: false,
})
export class LeapV2ScaleSliderComponent {
  @Input() value = 0;
  @Input() min = 0;
  @Input() max = 4;
  @Input() step = 1;
  /** Tick labels shown under the track (and used as the value text in 'label' mode). */
  @Input() labels: ReadonlyArray<string> = [];
  /**
   * 'label' shows labels[value]; 'percent' shows `${value}%`; 'number' shows the raw value — for scales whose
   * ticks are end captions only (e.g. ['Poor', 'High']) rather than one label per step.
   */
  @Input() display: 'label' | 'percent' | 'number' = 'label';
  /** When true the value text steps red → green across the range. */
  @Input() colourRamp = false;
  @Input() disabled = false;

  @Output() valueChange = new EventEmitter<number>();

  get fillPercent(): number {
    const span = this.max - this.min;
    return span <= 0 ? 0 : ((this.value - this.min) / span) * 100;
  }

  get trackBackground(): string {
    const pct = this.fillPercent;
    return `linear-gradient(to right, #4b3db5 ${pct}%, #e0e0e0 ${pct}%)`;
  }

  get valueText(): string {
    if (this.display === 'percent') return `${this.value}%`;
    if (this.display === 'number') return String(this.value);
    // Min-relative, so a 1-based scale (SEMH) reads its labels correctly while every 0-based
    // scale here (key stage, percent ticks) is unaffected.
    return this.labels[this.value - this.min] ?? String(this.value);
  }

  get valueColour(): string {
    if (!this.colourRamp) return '#4b3db5';
    const span = this.max - this.min;
    const ratio = span <= 0 ? 0 : (this.value - this.min) / span;
    return COLOUR_RAMP[Math.round(ratio * (COLOUR_RAMP.length - 1))];
  }

  onInput(event: Event): void {
    if (this.disabled) return;
    this.value = Number((event.target as HTMLInputElement).value);
    this.valueChange.emit(this.value);
  }

  private clamp(n: number): number {
    return Math.min(this.max, Math.max(this.min, n));
  }

  /** Live sync from the editable percent field while the user types a valid number. */
  onNumberInput(event: Event): void {
    if (this.disabled) return;
    const raw = (event.target as HTMLInputElement).value;
    if (raw === '') return; // don't force a value while the field is being cleared
    const n = Number(raw);
    if (Number.isNaN(n)) return;
    this.value = this.clamp(n);
    this.valueChange.emit(this.value);
  }

  /** Normalise an empty or out-of-range entry when the field loses focus. */
  onNumberBlur(event: Event): void {
    if (this.disabled) return;
    const input = event.target as HTMLInputElement;
    const n = Number(input.value);
    this.value = input.value === '' || Number.isNaN(n) ? this.value : this.clamp(n);
    input.value = String(this.value); // snap the displayed text back to the clamped value
    this.valueChange.emit(this.value);
  }
}
